class PrimerryHero extends HTMLElement {
  constructor() {
    super();
    this.index = 0;
    this.timer = null;
    this.drag = null;
    this.blockClick = false;
    this.editorPaused = false;
    this.initialized = false;
    this.handleVisibilityChange = () => (document.hidden ? this.stop() : this.start());
  }

  connectedCallback() {
    document.addEventListener('visibilitychange', this.handleVisibilityChange);

    // The script tag is rendered before the slider markup, so connectedCallback can fire
    // before the track/slides/dots exist. Wait for the document to be parsed before wiring up.
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.init(), { once: true });
    } else {
      this.init();
    }
  }

  disconnectedCallback() {
    this.stop();
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
  }

  init() {
    if (!this.isConnected) return;
    if (this.initialized) {
      this.start();
      return;
    }

    this.track = this.querySelector('[data-hero-track]');
    this.slides = [...this.querySelectorAll('[data-hero-slide]')];
    this.dots = [...this.querySelectorAll('[data-hero-dot]')];
    if (!this.track) return;

    this.initialized = true;
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.dots.forEach((dot) => {
      dot.addEventListener('click', () => this.goTo(Number(dot.dataset.heroDot), true));
    });

    this.track.addEventListener('scroll', () => this.onScroll(), { passive: true });
    this.track.addEventListener('pointerdown', (event) => this.onPointerDown(event));
    this.track.addEventListener('pointermove', (event) => this.onPointerMove(event));
    this.track.addEventListener('pointerup', (event) => this.onPointerUp(event));
    this.track.addEventListener('pointercancel', (event) => this.onPointerUp(event));
    this.track.addEventListener('click', (event) => this.onClick(event), true);
    this.track.addEventListener('dragstart', (event) => event.preventDefault());
    this.track.addEventListener('touchstart', () => this.stop(), { passive: true });
    this.track.addEventListener('touchend', () => this.start(), { passive: true });
    this.addEventListener('focusin', (event) => this.onFocusIn(event));
    this.addEventListener('focusout', () => this.start());
    this.addEventListener('shopify:block:select', (event) => this.onBlockSelect(event));
    this.addEventListener('shopify:block:deselect', () => this.onBlockDeselect());

    this.setActive(0);
    this.start();
  }

  start() {
    this.stop();
    if (!this.initialized || this.editorPaused || this.drag || document.hidden) return;
    if (this.dataset.autoplay !== 'true' || this.reducedMotion || this.slides.length < 2) return;
    const delay = Math.max(Number(this.dataset.speed) || 5, 1) * 1000;
    this.timer = window.setInterval(() => this.goTo((this.index + 1) % this.slides.length), delay);
  }

  stop() {
    if (this.timer) window.clearInterval(this.timer);
    this.timer = null;
  }

  onFocusIn(event) {
    // Pause only for keyboard focus so mouse clicks on the dots don't stop auto scroll.
    try {
      if (event.target.matches(':focus-visible')) this.stop();
    } catch (error) {
      this.stop();
    }
  }

  onBlockSelect(event) {
    const index = this.slides.indexOf(event.target);
    if (index < 0) return;
    this.editorPaused = true;
    this.stop();
    this.goTo(index);
  }

  onBlockDeselect() {
    this.editorPaused = false;
    this.start();
  }

  onPointerDown(event) {
    if (event.pointerType !== 'mouse' || event.button !== 0 || this.slides.length < 2) return;
    this.drag = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startScrollLeft: this.track.scrollLeft,
      moved: false
    };
    this.track.setPointerCapture(event.pointerId);
    this.track.classList.add('is-dragging');
    this.stop();
  }

  onPointerMove(event) {
    if (!this.drag || event.pointerId !== this.drag.pointerId) return;
    const distance = event.clientX - this.drag.startX;
    if (Math.abs(distance) > 5) this.drag.moved = true;
    if (!this.drag.moved) return;
    event.preventDefault();
    this.track.scrollLeft = this.drag.startScrollLeft - distance;
  }

  onPointerUp(event) {
    if (!this.drag || event.pointerId !== this.drag.pointerId) return;
    const { moved, startScrollLeft } = this.drag;
    this.blockClick = moved;
    this.track.classList.remove('is-dragging');
    if (this.track.hasPointerCapture(event.pointerId)) this.track.releasePointerCapture(event.pointerId);
    this.drag = null;

    const width = this.track.clientWidth || 1;
    const startIndex = Math.round(startScrollLeft / width);
    const delta = this.track.scrollLeft - startScrollLeft;
    let nextIndex = Math.round(this.track.scrollLeft / width);
    // A short flick (> 15% of the width) is enough to move one slide in the drag direction.
    if (moved && nextIndex === startIndex && Math.abs(delta) > width * 0.15) {
      nextIndex = startIndex + Math.sign(delta);
    }
    this.goTo(Math.max(0, Math.min(nextIndex, this.slides.length - 1)));
    this.start();
  }

  onClick(event) {
    if (!this.blockClick) return;
    event.preventDefault();
    event.stopPropagation();
    this.blockClick = false;
  }

  goTo(index, restart = false) {
    if (!this.track || !this.slides[index]) return;
    this.track.scrollTo({ left: this.track.clientWidth * index, behavior: this.reducedMotion ? 'auto' : 'smooth' });
    this.setActive(index);
    if (restart) this.start();
  }

  onScroll() {
    if (!this.track?.clientWidth || this.drag) return;
    const nextIndex = Math.round(this.track.scrollLeft / this.track.clientWidth);
    this.setActive(Math.max(0, Math.min(nextIndex, this.slides.length - 1)));
  }

  setActive(index) {
    this.index = index;
    this.dots.forEach((dot, dotIndex) => {
      const active = dotIndex === index;
      dot.classList.toggle('is-active', active);
      if (active) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    });
  }
}

if (!customElements.get('primerry-hero')) customElements.define('primerry-hero', PrimerryHero);
